import { useRef, useState } from "react";

import { z } from "zod";

import {
  type ProjectInvitation,
  type ProjectSharingBaseRole,
  ProjectSharingBaseRoles,
  useDeleteProjectInvitation,
  useUpdateProjectInvitation,
} from "../../../../../hooks/useProjectSharing";
import type { InvitationID } from "../../../../../hooks/useProjectViewLink";
import { InvitationRow } from "./InvitationRow";

interface InvitationRowProps {
  invitation: ProjectInvitation;
}

const baseRoleSchema = z.enum([...Object.values(ProjectSharingBaseRoles)] as [
  ProjectSharingBaseRole,
  ...ProjectSharingBaseRole[],
]);

const projectInvitationSchema = z.object({
  baseRole: baseRoleSchema,
});

export const InvitationRowProject = ({ invitation }: InvitationRowProps) => {
  const animationPromiseResolver = useRef<(() => void) | null>(null);
  const {
    mutateAsync: mutateUpdateProjectInvitation,
    isPending: isUpdatePending,
    isError: isUpdateError,
  } = useUpdateProjectInvitation({});
  const {
    mutateAsync: mutateDeleteProjectInvitation,
    isPending: isDeletePending,
    isError: isDeleteError,
    removeInvitationFromCache,
  } = useDeleteProjectInvitation({
    onSuccess: async () => {
      setDeleteConfirmationOpen(false);
      const animationPromise = new Promise<void>((resolve) => {
        animationPromiseResolver.current = resolve;
      });
      await animationPromise;
      removeInvitationFromCache(invitation.invitationId);
    },
    onError: () => {
      setDeleteConfirmationOpen(false);
    },
  });

  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);

  return (
    <InvitationRow
      invitation={invitation}
      schema={projectInvitationSchema}
      onDelete={() => {
        return mutateDeleteProjectInvitation(invitation.invitationId ?? ("" as InvitationID));
      }}
      deleteConfirmationOpen={deleteConfirmationOpen}
      onUpdate={(data) => {
        return mutateUpdateProjectInvitation({
          projectInvitationId: invitation.invitationId ?? ("" as InvitationID),
          baseRole: data.baseRole as ProjectSharingBaseRole,
        });
      }}
      onOpenDeleteConfirmation={() => setDeleteConfirmationOpen(true)}
      onCloseDeleteConfirmation={() => setDeleteConfirmationOpen(false)}
      animationPromiseResolver={animationPromiseResolver}
      isPending={isDeletePending || isUpdatePending}
      isError={isDeleteError || isUpdateError}
    />
  );
};
